import axios, {AxiosResponse} from 'axios'
import {API_URL} from '../../../../../_metronic/helpers'
import {ReportQuery} from '../../../../pages/reports/invoice-reports/core/_models'
import {PartnerCommission} from '../../partner-commission/core/_models'
import {
  AuditList,
  BusinessList,
  EventList,
  ExportKind,
  InvoiceLookup,
  MonthlyReport,
  SessionList,
  WorkspaceContext,
  WorkspaceErrors,
  WorkspaceEvent,
  WorkspaceFunnel,
  WorkspaceHealth,
  WorkspaceOverview,
  WorkspaceRejections,
  WorkspaceSecret,
  WorkspaceUsage,
} from './_models'

const WORKSPACE_URL = `${API_URL}/partner-workspace`

/** The system administrator opens another business's workspace with its id; a partner's user never sends one. */
const scopeParams = (companyId: number) => (companyId > 0 ? {companyId} : {})

/** The report page's period, the way api/reports/invoices takes it. */
export const periodParams = (query: ReportQuery) => ({
  period: query.period,
  key: query.period === 'range' ? undefined : query.key || undefined,
  from: query.period === 'range' ? query.from : undefined,
  to: query.period === 'range' ? query.to : undefined,
})

const get = <T>(path: string, params: object, signal?: AbortSignal) =>
  axios.get(`${WORKSPACE_URL}/${path}`, {params, signal}).then((response: AxiosResponse<T>) => response.data)

const post = <T>(path: string, companyId: number, body?: object) =>
  axios
    .post(`${WORKSPACE_URL}/${path}`, body ?? {}, {params: scopeParams(companyId)})
    .then((response: AxiosResponse<T>) => response.data)

/** Whether the side menu offers the workspace for the selected business (never a refusal). */
export const getWorkspaceAvailable = (signal?: AbortSignal) =>
  get<{available: boolean}>('available', {}, signal)

export const getWorkspaceContext = (companyId: number, signal?: AbortSignal) =>
  get<WorkspaceContext>('context', scopeParams(companyId), signal)

export const getWorkspaceOverview = (companyId: number, query: ReportQuery, signal?: AbortSignal, refresh?: boolean) =>
  get<WorkspaceOverview>(
    'overview',
    {...scopeParams(companyId), ...periodParams(query), refresh: refresh || undefined},
    signal
  )

export const getWorkspaceFunnel = (companyId: number, query: ReportQuery, signal?: AbortSignal) =>
  get<WorkspaceFunnel>('funnel', {...scopeParams(companyId), ...periodParams(query)}, signal)

export type SessionFilters = {status: string; stage: string; q: string; page: number}

export const getWorkspaceSessions = (companyId: number, filters: SessionFilters, signal?: AbortSignal) =>
  get<SessionList>(
    'sessions',
    {
      ...scopeParams(companyId),
      status: filters.status || undefined,
      stage: filters.stage || undefined,
      q: filters.q || undefined,
      page: filters.page,
      pageSize: WORKSPACE_PAGE_SIZE,
    },
    signal
  )

export type BusinessFilters = {q: string; health: string; sort: string; page: number; pageSize?: number}

export const getWorkspaceBusinesses = (
  companyId: number,
  query: ReportQuery,
  filters: BusinessFilters,
  signal?: AbortSignal
) =>
  get<BusinessList>(
    'businesses',
    {
      ...scopeParams(companyId),
      ...periodParams(query),
      q: filters.q || undefined,
      health: filters.health || undefined,
      sort: filters.sort || undefined,
      page: filters.page,
      pageSize: filters.pageSize || WORKSPACE_PAGE_SIZE,
    },
    signal
  )

export const getWorkspaceErrors = (companyId: number, query: ReportQuery, signal?: AbortSignal) =>
  get<WorkspaceErrors>('errors', {...scopeParams(companyId), ...periodParams(query)}, signal)

/** Invoices API v2 refused before saving them (backend 1.0.0.777). */
export const getWorkspaceRejections = (
  companyId: number,
  query: ReportQuery,
  signal?: AbortSignal
) =>
  get<WorkspaceRejections>(
    'rejections',
    {...scopeParams(companyId), ...periodParams(query)},
    signal
  )

export const findWorkspaceInvoices = (companyId: number, q: string, businessId: number, signal?: AbortSignal) =>
  get<InvoiceLookup>(
    'invoices',
    {...scopeParams(companyId), q, businessId: businessId > 0 ? businessId : undefined},
    signal
  )

export const getWorkspaceHealth = (companyId: number, signal?: AbortSignal) =>
  get<WorkspaceHealth>('health', scopeParams(companyId), signal)

export const getWorkspaceEvents = (companyId: number, status: string, page: number, signal?: AbortSignal) =>
  get<EventList>(
    'events',
    {...scopeParams(companyId), status: status || undefined, page, pageSize: WORKSPACE_PAGE_SIZE},
    signal
  )

export const resendWorkspaceEvent = (companyId: number, eventId: number) =>
  post<WorkspaceEvent>(`events/${eventId}/resend`, companyId)

export const sendWorkspaceTestEvent = (companyId: number) => post<WorkspaceEvent>('webhook/test', companyId)

export const updateWorkspaceWebhook = (companyId: number, webhookUrl: string, webhookEnabled: boolean) =>
  post<WorkspaceSecret>('webhook', companyId, {webhookUrl: webhookUrl.trim() || null, webhookEnabled})

export const rotateWorkspaceWebhookSecret = (companyId: number) => post<WorkspaceSecret>('webhook/secret', companyId)

export const rotateWorkspaceApiSecret = (companyId: number) => post<WorkspaceSecret>('credentials/secret', companyId)

export const getWorkspaceAudit = (companyId: number, page: number, signal?: AbortSignal) =>
  get<AuditList>('audit', {...scopeParams(companyId), page, pageSize: WORKSPACE_PAGE_SIZE}, signal)

export const getWorkspaceUsage = (companyId: number, fromMonth: string, toMonth: string, signal?: AbortSignal) =>
  get<WorkspaceUsage>('usage', {...scopeParams(companyId), fromMonth, toMonth}, signal)

export const getWorkspaceReport = (companyId: number, month: string, signal?: AbortSignal) =>
  get<MonthlyReport>('report', {...scopeParams(companyId), month: month || undefined}, signal)

/** «پورسانت» as the partner sees it (MegaPayBackend 1.0.0.761). */
export const getWorkspaceCommission = (companyId: number, signal?: AbortSignal) =>
  get<PartnerCommission>('commission', scopeParams(companyId), signal)

export type ExportOptions = {query?: ReportQuery; month?: string; fromMonth?: string; toMonth?: string}

/**
 * An Excel workbook from the server (authenticated, so fetched as a blob, never a plain link), saved
 * under the name the server gives it.
 */
export const downloadWorkspaceExport = async (companyId: number, kind: ExportKind, options: ExportOptions) => {
  const response: AxiosResponse<Blob> = await axios.get(`${WORKSPACE_URL}/export`, {
    params: {
      ...scopeParams(companyId),
      kind,
      ...(options.query ? periodParams(options.query) : {}),
      month: options.month || undefined,
      fromMonth: options.fromMonth || undefined,
      toMonth: options.toMonth || undefined,
    },
    responseType: 'blob',
    timeout: 180000,
  })
  const disposition = String(response.headers?.['content-disposition'] || '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const fileName = match ? decodeURIComponent(match[1]) : `asa-partner-${kind}.xlsx`
  const href = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}

export const WORKSPACE_PAGE_SIZE = 20

/** Every running period's figures are asked again every minute; the server keeps them two. */
export const WORKSPACE_LIVE_REFETCH_MS = 60_000

export const workspaceKeys = {
  all: ['partner-workspace'] as const,
  available: (companyId: number) => ['partner-workspace', 'available', companyId] as const,
  context: (companyId: number) => ['partner-workspace', 'context', companyId] as const,
  part: (part: string, companyId: number, query: ReportQuery, extra?: object) =>
    [
      'partner-workspace',
      part,
      companyId,
      query.period,
      query.period === 'range' ? `${query.from}-${query.to}` : query.key || 'current',
      extra ?? {},
    ] as const,
  plain: (part: string, companyId: number, extra?: object) =>
    ['partner-workspace', part, companyId, extra ?? {}] as const,
}

/** The server's refusal code (COMPANY_REQUIRED, partner_not_found, …), when there is one. */
export const readWorkspaceErrorCode = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {code?: string} | undefined
    return data?.code || ''
  }

  return ''
}

/** A refusal or a failure in the server's own words, never a raw exception. */
export const readWorkspaceError = (error: unknown, fallback = 'خواندن اطلاعات انجام نشد. چند لحظه دیگر دوباره تلاش کنید.') => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'
    }

    const data = error.response.data as {message?: string} | Blob | undefined
    if (data && !(data instanceof Blob) && data.message) {
      return data.message
    }
  }

  return fallback
}

/** The field a refusal names (fields: {webhookUrl: '…'}), for the form beside it. */
export const readWorkspaceFieldError = (error: unknown, field: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {fields?: Record<string, string>; message?: string} | undefined
    if (data?.fields && data.fields[field]) return data.fields[field]
  }

  return ''
}
